import { CreateMutationOptions, CreateQueryOptions, createMutation, createQuery } from 'react-query-kit';
import { flatMap, map } from 'lodash';

import type { FetchEndpoint } from '../build-endpoint/build-endpoint.model';
import type { EndpointMethodKeys, EndpointsShape, EndpointsShapeWithNamespaces } from '../create-api.model';

import { querySelectors } from './create-api-hooks.utils';

type ExtraOptions = {
    /** Extra option to store constants for a hook */
    models?: Record<string, any>;
};

type QuerySelector<Res, Ret = any> = (response: Res) => Ret;
type QueryOptions<Res, Req> = Omit<CreateQueryOptions<Res, Req>, 'queryKey' | 'fetcher'> &
    /** Allow optional override of the queryKey and fetcher */
    Partial<Pick<CreateQueryOptions<Res, Req>, 'queryKey' | 'fetcher'>> & {
        /** Extra option to store reusable selectors for a hook */
        select?: Record<string, QuerySelector<Res>>;
    } & ExtraOptions;
type MutationOptions<Res, Req> = Omit<CreateMutationOptions<Res, Req>, 'mutationKey' | 'mutationFn'> &
    /** Allow optional override of the mutationKey and mutationFn */
    Partial<Pick<CreateMutationOptions<Res, Req>, 'mutationKey' | 'mutationFn'>> &
    ExtraOptions;

type HookPrefix<T extends EndpointMethodKeys = EndpointMethodKeys> = `use${Capitalize<T>}`;
type HookName<M extends EndpointMethodKeys, K extends string> = `${HookPrefix<M>}${Capitalize<K>}`;
type InferRes<T> = T extends FetchEndpoint<infer Res, any> ? Res : void;
type InferReq<T> = T extends FetchEndpoint<any, infer Req> ? Req : void;
type InferResponseRequest<T extends FetchEndpoint<any, any>> = T extends FetchEndpoint<infer Res, infer Req>
    ? { Res: Res; Req: Req }
    : never;

type ApiHookOptions<T extends EndpointsShape> = {
    [M in EndpointMethodKeys]: {
        [K in keyof T[M] as HookName<M, K & string>]?: M extends 'get'
            ? QueryOptions<InferRes<T[M][K]>, InferReq<T[M][K]>>
            : MutationOptions<InferRes<T[M][K]>, InferReq<T[M][K]>>;
    };
}[EndpointMethodKeys];

type GetSelectors<O, K> = O extends {
    [P in `useGet${Capitalize<string & K>}`]?: { select?: infer S };
}
    ? S extends Record<string, QuerySelector<any>>
        ? S
        : {}
    : {};
type GetExtraOptions<O, K, M extends EndpointMethodKeys> = O extends {
    [P in `${HookPrefix<M>}${Capitalize<string & K>}`]?: { models?: infer M };
}
    ? { models: M }
    : Record<string, never>;

type ApiHooks<T extends EndpointsShape, O extends ApiHookOptions<T> = ApiHookOptions<T>> = UnionToIntersection<
    {
        [M in EndpointMethodKeys]: {
            [K in keyof T[M] as HookName<M, string & K>]: T[M][K] extends FetchEndpoint<infer Res, infer Req>
                ? M extends 'get'
                    ? ReturnType<typeof createQuery<Res, Req, Error>> & {
                          /** Selectors for the API hook */
                          select: GetSelectors<O, K>;
                      } & GetExtraOptions<O, K, M>
                    : ReturnType<typeof createMutation<Res, Req, Error>> & GetExtraOptions<O, K, M>
                : never;
        };
    }[EndpointMethodKeys]
> & {
    /** Global query selectors; to be used against any API hook or to create a custom selector */
    select: typeof querySelectors;
};

const METHOD_TYPES = ['get', 'post', 'patch', 'delete', 'put'] as const satisfies EndpointMethodKeys[];
const EXTRA_OPTIONS_CONFIG = {
    global: [{ key: 'models', fallback: {} }],
    query: [{ key: 'select', fallback: {} }],
};
const EXTRA_OPTIONS = flatMap(EXTRA_OPTIONS_CONFIG, options => map(options, 'key'));
const METHOD_PREFIXES: Record<(typeof METHOD_TYPES)[number], HookPrefix<EndpointMethodKeys>> = {
    get: 'useGet',
    post: 'usePost',
    patch: 'usePatch',
    put: 'usePut',
    delete: 'useDelete',
};

type ApiHookOptionsForNamespacedEndpoints<T extends EndpointsShapeWithNamespaces> = {
    [M in EndpointMethodKeys]: {
        [NS in keyof T & string as T[NS] extends Record<M, any> ? HookName<M, NS> : never]?: T[NS] extends Record<
            M,
            infer E
        >
            ? E extends FetchEndpoint<infer Res, infer Req>
                ? M extends 'get'
                    ? QueryOptions<Res, Req>
                    : MutationOptions<Res, Req>
                : never
            : never;
    };
}[EndpointMethodKeys];

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never;

type ApiHooksForNamespacedEndpoints<
    T extends EndpointsShapeWithNamespaces,
    O = ApiHookOptionsForNamespacedEndpoints<T>,
> = UnionToIntersection<
    {
        [M in EndpointMethodKeys]: {
            [NS in keyof T & string as T[NS] extends Record<M, any> ? HookName<M, NS> : never]: T[NS] extends Record<
                M,
                infer E
            >
                ? E extends FetchEndpoint<infer Res, infer Req>
                    ? M extends 'get'
                        ? ReturnType<typeof createQuery<Res, Req, Error>> & {
                              select: GetSelectors<O, NS>;
                          } & GetExtraOptions<O, NS, M>
                        : ReturnType<typeof createMutation<Res, Req, Error>> & GetExtraOptions<O, NS, M>
                    : never
                : never;
        };
    }[EndpointMethodKeys]
> & {
    /** Global query selectors; to be used against any API hook or to create a custom selector */
    select: typeof querySelectors;
};

export type {
    ApiHookOptions,
    ApiHooks,
    InferResponseRequest,
    ApiHookOptionsForNamespacedEndpoints,
    ApiHooksForNamespacedEndpoints,
    EndpointMethodKeys,
};
export { EXTRA_OPTIONS, EXTRA_OPTIONS_CONFIG, METHOD_PREFIXES, METHOD_TYPES };
